import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import {
  Accordion,
  FormInput,
  handleSuccessToast,
} from 'src/components/molecules';
import {
  ReportDetailsVm,
  reportDetailsVm,
} from 'src/components/pages/Case/Case.slice';
import { useUpdateReportRelationshipToOrganisationMutation } from 'src/graphql';
import { FormInputVariant, FormState } from 'src/types';
import {
  ReporterDetailsContainerStyled,
  ReporterDetailsFieldsContainerStyled,
  ReporterDetailsInnerContainerStyled,
  ReporterDetailsInputFieldContainerStyled,
  ReporterNotesFieldContainerStyled,
} from './NewReporterDetails.styles';
import { NotificationEmailInputField } from './sub-components/NotificationEmailInputField';

interface ReporterDetailsProps {
  reportId: string;
  testVm?: ReportDetailsVm;
  'data-testid'?: string;
}

export const NewReporterDetails = ({
  reportId,
  testVm,
  ...props
}: ReporterDetailsProps) => {
  const {
    reporter,
    notes,
    relationshipTypes,
    relationshipType,
    updateRelationshipToOrgEnabled,
    permissions: { canViewReportDetailsPii, canUpdateRelationshipToOrg },
  } = useSelector(reportDetailsVm({ reportId, testVm }));

  const queryClient = useQueryClient();
  const updateReporterRelationshipToOrganisation =
    useUpdateReportRelationshipToOrganisationMutation({
      onSuccess: () => {
        queryClient.invalidateQueries(['CaseById']);
        handleSuccessToast({
          toastMessage: 'Relationship to organisation updated successfully',
        });
      },
      onError: () => {
        throw new Error('Error updating reporter relationship to organisation');
      },
    });

  const setInputState = () =>
    canViewReportDetailsPii ? FormState.READONLY : FormState.DISABLED;

  const setRelationshipToOrgState = () => {
    if (!canViewReportDetailsPii) {
      return FormState.DISABLED;
    }
    if (canUpdateRelationshipToOrg) {
      return FormState.DEFAULT;
    }
    return FormState.READONLY;
  };

  return (
    <ReporterDetailsContainerStyled>
      <Accordion
        topBorderRadius="6px"
        bottomBorderRadius="6px"
        title={'Reporter Information'}
        defaultOpen={false}
        data-testid={props['data-testid']}
        last
      >
        <ReporterDetailsInnerContainerStyled>
          <ReporterDetailsFieldsContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              <FormInput
                isRequired={false}
                label="Reporter name"
                data-testid="reporter-name"
                variant={FormInputVariant.INPUT}
                state={setInputState()}
                value={reporter?.name}
              />
            </ReporterDetailsInputFieldContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              <FormInput
                isRequired={false}
                label="Reporter email"
                data-testid="reporter-email"
                variant={FormInputVariant.INPUT}
                state={setInputState()}
                value={reporter?.email}
              />
            </ReporterDetailsInputFieldContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              <FormInput
                isRequired={false}
                label="Reporter role"
                data-testid="reporter-role"
                variant={FormInputVariant.INPUT}
                state={setInputState()}
                value={reporter?.role}
              />
            </ReporterDetailsInputFieldContainerStyled>
          </ReporterDetailsFieldsContainerStyled>
          <ReporterDetailsFieldsContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              <FormInput
                isRequired={false}
                label="Employee ID"
                data-testid="reporter-eid"
                variant={FormInputVariant.INPUT}
                state={setInputState()}
                value={reporter?.eid}
              />
            </ReporterDetailsInputFieldContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              {updateRelationshipToOrgEnabled && canUpdateRelationshipToOrg ? (
                <FormInput
                  isRequired={false}
                  label="Relationship to organisation"
                  data-testid="relationship-to-organisation"
                  variant={FormInputVariant.DROPDOWN}
                  state={setRelationshipToOrgState()}
                  value={relationshipType}
                  options={relationshipTypes}
                  onChange={(option) => {
                    if (
                      canUpdateRelationshipToOrg &&
                      option &&
                      'value' in option
                    ) {
                      updateReporterRelationshipToOrganisation.mutate({
                        report: reportId,
                        relationshipToOrganisation: option.value as number,
                      });
                    }
                  }}
                />
              ) : (
                <FormInput
                  isRequired={false}
                  label="Relationship to organisation"
                  data-testid="relationship-to-organisation"
                  variant={FormInputVariant.INPUT}
                  state={setInputState()}
                  value={
                    updateRelationshipToOrgEnabled
                      ? relationshipType?.label
                      : reporter?.relationshipToOrganisation
                  }
                />
              )}
            </ReporterDetailsInputFieldContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              <FormInput
                isRequired={false}
                label="Reporter Department"
                data-testid="reporter-department"
                variant={FormInputVariant.INPUT}
                state={setInputState()}
                value={reporter?.department}
              />
            </ReporterDetailsInputFieldContainerStyled>
          </ReporterDetailsFieldsContainerStyled>
          <ReporterDetailsFieldsContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              <FormInput
                isRequired={false}
                label="Reporter Site"
                data-testid="reporter-site"
                variant={FormInputVariant.INPUT}
                state={setInputState()}
                value={reporter?.site}
              />
            </ReporterDetailsInputFieldContainerStyled>
            <ReporterDetailsInputFieldContainerStyled>
              <FormInput
                isRequired={false}
                label="Reporter Country"
                data-testid="reporter-country"
                variant={FormInputVariant.INPUT}
                state={setInputState()}
                value={reporter?.country}
              />
            </ReporterDetailsInputFieldContainerStyled>
            <NotificationEmailInputField reportId={reportId} testVm={testVm} />
          </ReporterDetailsFieldsContainerStyled>
          <ReporterNotesFieldContainerStyled>
            <FormInput
              isRequired={false}
              label="Reporter notes"
              data-testid="reporter-notes"
              variant={FormInputVariant.AREA}
              state={setInputState()}
              value={notes}
            />
          </ReporterNotesFieldContainerStyled>
        </ReporterDetailsInnerContainerStyled>
      </Accordion>
    </ReporterDetailsContainerStyled>
  );
};
